/**
 * Configuración Centralizada - Luciano Argolo Data Engineering Hub
 * 
 * Este archivo contiene TODA la información de contacto, nombres y URLs
 * para evitar duplicaciones en el código.
 * 
 * IMPORTANTE: Solo modifica aquí - no en los componentes individuales
 */

export const SITE_CONFIG = {
  // Información Personal
  personal: {
    firstName: 'Luciano',
    lastName: 'Argolo',
    fullName: 'Luciano Argolo',
    title: 'Sr Data Engineer',
    tagline: 'Experto en Data Engineering',
  },

  // Información de Contacto
  contact: {
    email: {
      main: 'LucianoAgustinArgolo@gmail.com',          // Email principal de contacto
      support: 'soporte@lucianoargolo.com',    // Email de soporte
      noreply: 'noreply@lucianoargolo.com',    // Email para notificaciones automáticas
      admin: 'admin@lucianoargolo.com',          // Email del administrador
      demo: 'demo@lucianoargolo.com',          // Email de usuario demo
    },
    phone: '+54 9 11 XXXX-XXXX',          // Teléfono (actualizar)
  },

  // URLs y Dominios
  urls: {
    website: 'https://lucianoargolo.com',
    api: 'https://lucianoargolo.com/api',
    localDev: 'http://localhost:3000',
    programaPDF: '/downloads/Programa-Completo.pdf',
  },

  // Redes Sociales
  social: {
    linkedin: 'https://www.linkedin.com/in/luciano-argolo/',
    instagram: 'https://www.instagram.com/lucianoargolo22/',
    tiktok: 'https://www.tiktok.com/@lucianoargolo0',
    github: 'https://github.com/LucianoArgolo22',
    twitter: 'https://x.com/Dataengine35280',
    youtube: 'https://www.youtube.com/@lucianoargolo1608',
  },

  // Información de Suscripción
  subscription: {
    monthlyPrice: 100.00,
    currency: 'USD',
    planName: 'Club de Data Engineering',
    benefits: [
      'Videos exclusivos semanales',
      'Comunidad privada en Discord',
      'Plantillas y recursos descargables',
      'Acceso a proyectos reales',
      'Q&A mensuales en vivo',
    ],
  },

  // Información de Mentoría — Modelo Híbrido
  mentoring: {
    sessionDuration: 60,
    currency: 'USD',
    policies: {
      maxReschedulesPerPackage: 2,
      rescheduleNoticeHours: 48,
      maxDaysBetweenSessions: 30,
    },
    diagnostic: {
      id: 'mentoring_diagnostic',
      name: 'Sesión Diagnóstico',
      sessions: 1,
      totalPrice: 100.00,
      description: 'Una sesión para conocer tu situación, definir objetivos y armar un plan. Sin compromiso.',
      features: [
        'Análisis de tu perfil y situación actual',
        'Definición de objetivos concretos',
        'Roadmap preliminar de carrera',
        'Recomendación del plan que mejor te sirve',
      ],
    },
    plans: [
      {
        id: 'mentoring_1m',
        name: '1 Mes',
        sessions: 4,
        months: 1,
        duration: '1 mes',
        totalPrice: 1000.00,
        monthlyPrice: 1000.00,
        pricePerSession: 250.00,
        savings: null,
        badge: null,
        badgeColor: null,
        availableSlots: 2,
        totalSlots: 5,
        features: [
          '4 sesiones 1:1 de 1 hora (1 por semana)',
          'Acceso a plataforma de desarrollo con ejercicios',
          'Roadmap personalizado de carrera',
          'Revisión de CV y portfolio',
          'Soporte por chat entre sesiones',
        ],
      },
      {
        id: 'mentoring_2m',
        name: '2 Meses',
        sessions: 8,
        months: 2,
        duration: '2 meses',
        totalPrice: 1900.00,
        monthlyPrice: 950.00,
        pricePerSession: 237.50,
        savings: 'Ahorrás $100',
        badge: 'POPULAR',
        badgeColor: 'blue',
        availableSlots: 2,
        totalSlots: 5,
        features: [
          '8 sesiones 1:1 de 1 hora (1 por semana)',
          'Acceso completo al Bootcamp de Databricks SQL ($495 USD de valor)',
          'Acceso a plataforma de desarrollo con ejercicios',
          'Roadmap personalizado de carrera',
          'Revisión de CV y portfolio',
          'Optimización de perfil de LinkedIn',
          'Preparación para entrevistas técnicas',
          'Soporte por chat entre sesiones',
        ],
      },
      {
        id: 'mentoring_3m',
        name: '3 Meses',
        sessions: 12,
        months: 3,
        duration: '3 meses',
        totalPrice: 2700.00,
        monthlyPrice: 900.00,
        pricePerSession: 225.00,
        savings: 'Ahorrás $300',
        badge: 'MEJOR VALOR',
        badgeColor: 'green',
        availableSlots: 1,
        totalSlots: 5,
        features: [
          '12 sesiones 1:1 de 1 hora (1 por semana)',
          'Acceso completo al Bootcamp de Databricks SQL ($495 USD de valor)',
          'Acceso a plataforma de desarrollo con ejercicios',
          'Roadmap personalizado de carrera',
          'Revisión de CV y portfolio',
          'Optimización de perfil de LinkedIn',
          'Preparación para entrevistas técnicas',
          'Simulacros de entrevistas con feedback',
          'Acompañamiento por WhatsApp ilimitado',
          'Desarrollo de marca personal',
          'Agenda de hábitos y plan de estudio',
        ],
      },
    ],
  },

  // Metadatos del Sitio
  seo: {
    title: 'Luciano Argolo - Data Engineering Hub',
    description: 'Aprende Data Engineering con proyectos reales. Cursos, mentoría y comunidad para convertirte en Data Engineer profesional.',
    keywords: 'data engineering, big data, python, sql, databricks, spark, airflow, aws',
  },

  // Información Legal
  legal: {
    copyright: `© ${new Date().getFullYear()} Luciano Argolo. Todos los derechos reservados.`,
    privacyPolicyUrl: '/privacy',
    termsOfServiceUrl: '/terms',
  },
};

/**
 * Helper functions para acceso rápido
 */
export const getMainEmail = () => SITE_CONFIG.contact.email.main;
export const getSupportEmail = () => SITE_CONFIG.contact.email.support;
export const getFullName = () => SITE_CONFIG.personal.fullName;
export const getWebsiteUrl = () => SITE_CONFIG.urls.website;
export const getCopyright = () => SITE_CONFIG.legal.copyright;

/**
 * Email helpers
 */
export const mailToLink = (email: string, subject?: string) => {
  const subjectParam = subject ? `?subject=${encodeURIComponent(subject)}` : '';
  return `mailto:${email}${subjectParam}`;
};

export const getMainEmailLink = (subject?: string) => 
  mailToLink(SITE_CONFIG.contact.email.main, subject);

export const getSupportEmailLink = (subject?: string) => 
  mailToLink(SITE_CONFIG.contact.email.support, subject);

export default SITE_CONFIG;


